import { useEffect } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles } from "lucide-react";

/**
 * Fires once per user per ISO week — a gentle in-app nudge the day after
 * the weekly follow-recap email goes out. Keeps the digest fresh even if
 * the email got filtered / the user missed it.
 *
 * Idempotency: localStorage key `pandax_recap_prompt_<week>` prevents
 * repeat shows in the same browser session. The backend endpoint returns
 * `show: true` only while a recap_sends row exists within the last 48h.
 */
export default function RecapPrompt() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !user.id) return;
    let cancelled = false;

    // Small delay so the toast doesn't collide with page-load spinners.
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/notifications/recap-prompt");
        if (cancelled || !data?.show || !data.week) return;

        const key = `pandax_recap_prompt_${user.id}_${data.week}`;
        if (typeof window !== "undefined" && window.localStorage.getItem(key)) {
          return;
        }
        try { window.localStorage.setItem(key, "1"); } catch { /* ignore */ }

        const n = data.recipients || 0;
        toast(
          n === 1
            ? "Someone new followed you this week"
            : `${n} new followers joined your circle this week`,
          {
            description: "Peek at their profiles and follow back the ones you match.",
            icon: <Sparkles size={16} className="text-rose-500" />,
            duration: 9000,
            action: {
              label: "See them",
              onClick: () => navigate("/notifications"),
            },
          }
        );
      } catch {
        /* endpoint may 401 during logout — safe to swallow */
      }
    }, 2500);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user, navigate]);

  return null;
}
